import { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from "react";
import { setOptedOut as setAnalyticsOptedOut } from "@/lib/analytics";

export type TextSize = "small" | "medium" | "large";

export interface GameSettings {
  masterVolume: number;    // 0–100
  musicMuted: boolean;
  textSize: TextSize;
  autoSave: boolean;
  fullScreen: boolean;
  /**
   * Player opt-out for anonymous gameplay analytics. When true, the
   * analytics layer in src/lib/analytics.ts short-circuits every
   * `track()` call. Default false (opt-in by default; nothing collected
   * is PII). Toggle exposed in SettingsPanel; details at /privacy.
   */
  analyticsOptOut: boolean;
}

/**
 * Default text size: "small" on every device. The text-size class sets the
 * ROOT font-size (see .text-size-* in index.css), which scales all rem-based
 * UI globally — so a "small" default keeps the home map, the region
 * dropdowns, and the chrome compact (the larger sizes noticeably enlarge
 * everything). Players can still pick medium/large in the typography
 * popover. Only used when the user has no saved preference yet.
 */
function defaultTextSize(): TextSize {
  return "small";
}

const DEFAULTS: GameSettings = {
  masterVolume: 50,
  musicMuted: false,
  textSize: "small",
  autoSave: true, // always on, not user-configurable
  fullScreen: false,
  analyticsOptOut: false,
};

const STORAGE_KEY = "chronicles_settings";

function load(): GameSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULTS, textSize: defaultTextSize() };
    const parsed = JSON.parse(raw);
    // If a previous session saved before device-aware defaults landed and
    // never explicitly set a field, fall through to the device default.
    return {
      ...DEFAULTS,
      textSize: parsed.textSize ?? defaultTextSize(),
      ...parsed,
    };
  } catch {
    return { ...DEFAULTS, textSize: defaultTextSize() };
  }
}

function save(s: GameSettings) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
}

interface SettingsContextValue {
  settings: GameSettings;
  update: (patch: Partial<GameSettings>) => void;
}

const SettingsContext = createContext<SettingsContextValue>({
  settings: DEFAULTS,
  update: () => {},
});

/**
 * useSettings — single source of truth for all player-facing toggles.
 *
 * Every UI surface that exposes a setting MUST read & write through
 * this hook. Three places do today, and they are wired to the same
 * underlying store, so changes in any one immediately reflect in the
 * others without further work:
 *
 *  - SettingsPanel (the cog-icon dialog) — full set: master volume,
 *    music mute, text size, full screen, plus actions (replay
 *    tutorial, reset progress, about).
 *  - InlineHeaderControls (the cluster on the play header) — quick
 *    access to music mute and the settings dialog launcher.
 *  - TypographyControl (the Type-icon popover in the header cluster)
 *    — text size pills.
 *
 * If you add a new setting and a new control surface, route it
 * through useSettings to keep all surfaces showing the same value.
 */
export function useSettings() {
  return useContext(SettingsContext);
}

export function SettingsProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<GameSettings>(load);

  const update = useCallback((patch: Partial<GameSettings>) => {
    setSettings((prev) => {
      const next = { ...prev, ...patch };
      save(next);
      return next;
    });
  }, []);

  // Push the opt-out state into the analytics layer whenever it changes.
  // The boot-time `initAnalytics()` in main.tsx reads localStorage once
  // before React mounts; this useEffect handles the runtime toggle from
  // the SettingsPanel without requiring a page reload.
  useEffect(() => {
    setAnalyticsOptedOut(settings.analyticsOptOut);
  }, [settings.analyticsOptOut]);

  // Apply text size to document root
  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove("text-size-small", "text-size-medium", "text-size-large");
    root.classList.add(`text-size-${settings.textSize}`);
  }, [settings.textSize]);

  // The story column is always rendered at full ("wide") width — the
  // per-user Reading Width control was removed (2026-06). Apply the wide
  // class once and strip any stale narrow/medium class a returning player
  // might still carry in localStorage from a previous session.
  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove("reading-width-narrow", "reading-width-medium");
    root.classList.add("reading-width-wide");
  }, []);

  // Fullscreen API sync (with webkit fallback for older Safari / macOS)
  useEffect(() => {
    const el = document.documentElement as HTMLElement & { webkitRequestFullscreen?: () => Promise<void> };
    const doc = document as Document & { webkitFullscreenElement?: Element; webkitExitFullscreen?: () => Promise<void> };
    const isFullscreen = !!(document.fullscreenElement || doc.webkitFullscreenElement);

    if (settings.fullScreen) {
      if (!isFullscreen) {
        (el.requestFullscreen?.() ?? el.webkitRequestFullscreen?.())?.catch?.(() => {});
      }
    } else {
      if (isFullscreen) {
        (document.exitFullscreen?.() ?? doc.webkitExitFullscreen?.())?.catch?.(() => {});
      }
    }
  }, [settings.fullScreen]);

  // Listen for ESC / external fullscreen exit → sync toggle OFF (both standard + webkit)
  useEffect(() => {
    const doc = document as Document & { webkitFullscreenElement?: Element };
    const handler = () => {
      const isFullscreen = !!(document.fullscreenElement || doc.webkitFullscreenElement);
      if (!isFullscreen) {
        setSettings((prev) => {
          if (prev.fullScreen) {
            const next = { ...prev, fullScreen: false };
            save(next);
            return next;
          }
          return prev;
        });
      }
    };
    document.addEventListener("fullscreenchange", handler);
    document.addEventListener("webkitfullscreenchange", handler);
    return () => {
      document.removeEventListener("fullscreenchange", handler);
      document.removeEventListener("webkitfullscreenchange", handler);
    };
  }, []);

  // Memoise the context value so consumers don't re-render on every
  // unrelated Provider render. `update` is already stable (useCallback,
  // []); `settings` is the only changing input. Without this useMemo,
  // every render created a fresh `{ settings, update }` literal and
  // React would consider the context value changed, re-rendering every
  // `useSettings()` consumer — and there are many across the app
  // (AmbientMusic, every header control, every settings panel surface,
  // every typography control, the volume sync, etc.).
  const contextValue = useMemo<SettingsContextValue>(
    () => ({ settings, update }),
    [settings, update],
  );

  return (
    <SettingsContext.Provider value={contextValue}>
      {children}
    </SettingsContext.Provider>
  );
}
